<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="d-inline-block brand-text-heading brand-green-dark--text mt-12">
          {{ toLocaleUpperCase($t('Checkout.Details.Title')) }}
        </h1>

        <v-divider class="mt-2 brand-green-dark" />
      </v-col>
    </v-row>

    <v-row v-if="AppType.isQRScanApp && AppQrData">
      <v-col
        cols="12"
        md="8"
        offset-md="2"
        offset-sm="1"
        sm="10"
      >
        <qr-code-order-data class="pa-0" />
      </v-col>
    </v-row>

    <!-- Order Details -->
    <v-row>
      <v-col
        cols="12"
        md="8"
        offset-md="2"
        offset-sm="1"
        sm="10"
      >
        <v-expansion-panels
          v-model="panel"
          accordion
          flat
        >
          <v-expansion-panel class="transparent">
            <v-expansion-panel-header
              class="px-0 rounded"
              color="transparent"
            >
              <div>
                <div class="d-flex align-center">
                  <div class="mr-6">
                    <v-badge
                      :value="cartProductsCount"
                      bordered
                      class="order-badge"
                      color="brand-beige-light"
                      dark
                      overlap
                    >
                      <template #badge>
                        <span class="brand-green-dark--text">{{ cartProductsCount }}</span>
                      </template>

                      <v-icon color="brand-green-dark">
                        mdi-cart
                      </v-icon>
                    </v-badge>
                  </div>

                  <span class="brand-text-heading">{{ toLocaleUpperCase($t('Checkout.Details.OrderDetails.Title')) }}</span>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="px-0">
              <v-list
                v-if="cartProductsCount"
                color="transparent"
                three-line
              >
                <template v-if="AppConfig.Cart.GroupByStore">
                  <template v-for="(store, storeIdx) in cartProductsGroupedByStore">
                    <div
                      :key="`cart-store-${storeIdx}`"
                      class="py-2 mt-2 d-flex align-center fill-width"
                    >
                      <v-avatar
                        v-if="AppConfig.Cart.GroupShowStoreLogo"
                        class="mr-2"
                        color="brand-green-dark"
                        size="40"
                        style="border: var(--v-brand-green-dark-base) solid 1px;"
                      >
                        <v-img :src="store.Logo" />
                      </v-avatar>

                      <div class="brand-text-title">
                        {{ toLocaleUpperCase(store.Name) }}
                      </div>

                      <v-spacer />

                      <div class="grey--text text-no-wrap pl-2">
                        <v-chip
                          v-if="storeIsOpened(store)"
                          class="brand-green-dark--text px-1 px-md-2"
                          color="brand-beige-dark"
                          dark
                          label
                        >
                          <v-icon
                            class="mr-1"
                            small
                          >
                            mdi-clock
                          </v-icon>
                          <span class="text-body">{{ storeTime(store) }}</span>
                        </v-chip>

                        <v-chip
                          v-else
                          class="brand-green-dark--text px-1 px-md-2"
                          color="brand-beige"
                          dark
                          label
                        >
                          <span class="text-body">{{ $t('Store.Status.Closed').toLocaleUpperCase($i18n.locale) }}</span>
                        </v-chip>
                      </div>
                    </div>

                    <template v-for="(product, productIdx) in store.Items">
                      <v-lazy
                        :key="`cart-store-${storeIdx}-product-lazy-${productIdx}`"
                        min-height="90"
                        transition="fade-transition"
                      >
                        <checkout-product-list-item
                          :key="`checkout-store-${storeIdx}-product-${productIdx}`"
                          :product="product"
                          :store-logo="AppConfig.Cart.ProductShowStoreLogo"
                          class="px-0"
                        />
                      </v-lazy>
                    </template>
                  </template>
                </template>

                <template v-else>
                  <template v-for="(product, idx) in AppCart.Items">
                    <v-lazy
                      :key="`cart-product-lazy-${idx}`"
                      min-height="110"
                      transition="fade-transition"
                    >
                      <checkout-product-list-item
                        :key="`checkout-product-${idx}`"
                        :product="product"
                        class="px-0"
                      />
                    </v-lazy>
                  </template>
                </template>
              </v-list>

              <div v-else>
                <div class="text-center">
                  <div class="mb-6">
                    <v-icon
                      color="brand-primary"
                      size="128"
                    >
                      mdi-basket-plus
                    </v-icon>
                  </div>

                  <h2>{{ $t('Cart.Message.Info.NoItems.Title') }}</h2>

                  <h4 class="font-weight-regular mt-4">
                    {{ $t('Cart.Message.Info.NoItems.Subtitle') }}
                  </h4>
                </div>

                <v-btn
                  :large="$vuetify.breakpoint.xsOnly"
                  :to="{name: 'Home'}"
                  :x-large="$vuetify.breakpoint.smAndUp"
                  block
                  class="text-capitalize mt-4"
                  color="brand-primary lighten-5 brand-primary--text"
                  dark
                  depressed
                >
                  {{ $t('Cart.Button.AddItems') }}
                </v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <template v-if="AppCart?.Items?.length">
      <!-- Scheduled Order -->
      <v-row v-if="AppConfig.Marketplace.ScheduledOrder.Active">
        <v-col
          cols="12"
          md="8"
          offset-md="2"
          offset-sm="1"
          sm="10"
        >
          <scheduled-order class="px-0" />
        </v-col>
      </v-row>

      <!-- Customer Details -->
      <v-row>
        <v-col
          cols="12"
          md="8"
          offset-md="2"
          offset-sm="1"
          sm="10"
        >
          <div class="d-flex align-center brand-green-dark--text">
            <v-icon
              class="mr-6"
              color="brand-green-dark"
            >
              mdi-account-circle
            </v-icon>

            <h2 class="d-inline-block brand-text-heading">
              {{ toLocaleUpperCase($t('Checkout.Details.CustomerDetails.Title')) }}
            </h2>
          </div>
        </v-col>

        <v-col
          cols="12"
          md="8"
          offset-md="2"
          offset-sm="1"
          sm="10"
        >
          <v-alert
            border="left"
            class="ma-0 text-body"
            colored-border
            dense
            icon="mdi-help"
            text
            type="info"
          >
            <template v-if="userIsAuthenticated">
              {{ $t('Checkout.Details.CustomerDetails.Notification.Logout', {user: user.Info.Firstname}) }}
              <a
                class=""
                @click="$bus.$emit('app:logout')"
              >
                {{ $t('Auth.Button.Logout') }}
              </a>
            </template>

            <template v-else>
              {{ $t('Checkout.Details.CustomerDetails.Notification.Login') }}
              <a
                class=""
                @click="$bus.$emit('login:show-dialog')"
              >
                <span
                  class="font-weight-medium"
                >{{ $t('Auth.Button.Login') }}</span>
              </a>.

              {{ $t('Checkout.Details.CustomerDetails.Notification.NoAccount') }}
              <a
                class=""
                @click="$bus.$emit('register:show-dialog')"
              >
                <span
                  class="font-weight-medium"
                >{{ $t('Auth.Button.Register') }}</span>
              </a>

              {{ $t('Checkout.Details.CustomerDetails.Notification.ContinueAsGuest') }}
            </template>
          </v-alert>
        </v-col>

        <v-col
          cols="12"
          md="8"
          offset-md="2"
          offset-sm="1"
          sm="10"
        >
          <v-form
            v-if="model"
            ref="form"
            v-model="isFormValid"
          >
            <v-container
              fluid
              pa-0
            >
              <template v-if="AppConfig.Marketplace.AllowGuestOrders || userIsAuthenticated">
                <!-- Customer Details -->
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-text-field
                      v-model="customerModel.Firstname"
                      :disabled="userIsAuthenticated"
                      :hint="$t('Checkout.Details.CustomerDetails.Field.Firstname.Hint')"
                      :label="$t('Checkout.Details.CustomerDetails.Field.Firstname.Label')"
                      :placeholder="$t('Checkout.Details.CustomerDetails.Field.Firstname.Placeholder')"
                      :rules="customerModelValidatorVuetifyFormFieldRules('Firstname')"
                      autocomplete="given-name"
                      clearable
                      color="brand-primary"
                      filled
                      flat
                      hide-details="auto"
                      min="2"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-text-field
                      v-model="customerModel.Lastname"
                      :disabled="userIsAuthenticated"
                      :hint="$t('Checkout.Details.CustomerDetails.Field.Lastname.Hint')"
                      :label="$t('Checkout.Details.CustomerDetails.Field.Lastname.Label')"
                      :placeholder="$t('Checkout.Details.CustomerDetails.Field.Lastname.Placeholder')"
                      :rules="customerModelValidatorVuetifyFormFieldRules('Lastname')"
                      autocomplete="family-name"
                      clearable
                      color="brand-primary"
                      filled
                      flat
                      hide-details="auto"
                      min="2"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-text-field
                      v-model="customerModel.Email"
                      :disabled="userIsAuthenticated"
                      :hint="$t('Checkout.Details.CustomerDetails.Field.Email.Hint')"
                      :label="$t('Checkout.Details.CustomerDetails.Field.Email.Label')"
                      :placeholder="$t('Checkout.Details.CustomerDetails.Field.Email.Placeholder')"
                      :rules="customerModelValidatorVuetifyFormFieldRules('Email')"
                      autocomplete="email"
                      clearable
                      color="brand-primary"
                      filled
                      flat
                      hide-details="auto"
                      type="email"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="6"
                  >
                    <vue-tel-input-vuetify
                      :disabled="userIsAuthenticated"
                      :hint="$t('Checkout.Details.CustomerDetails.Field.Phone.Hint')"
                      :init-country-value="getRegionCodeForCountryCode(customerModel?.PhonePrefix || '30')"
                      :init-phone-value="clone(customerModel?.Phone || '')"
                      :label="$t('Checkout.Details.CustomerDetails.Field.Phone.Label')"
                      :placeholder="$t('Checkout.Details.CustomerDetails.Field.Phone.Placeholder')"
                      :preferred-countries="['gr', 'cy']"
                      :rules="customerModelValidatorVuetifyFormFieldRules('Phone')"
                      :valid-characters-only="true"
                      autocomplete="mobile phone"
                      clearable
                      color="brand-primary"
                      filled
                      flat
                      hide-details="auto"
                      mode="significant"
                      name="phone"
                      text-field-classes="ml-2"
                      @input="(formattedNumber, phone) => {
                        customerModel.Phone = phone.number.significant
                        customerModel.PhonePrefix = phone.countryCode
                        validateForm()
                      }"
                    />
                  </v-col>
                </v-row>

                <!-- Delivery Details -->
                <v-row v-if="AppConfig.Marketplace.SupportsDelivery && AppCart.OrderType === OrderTypeEnum.DELIVERY">
                  <v-col cols="12">
                    <h2 class="d-inline-block text-h6 text-sm-h5 font-weight-bold">
                      {{ $t('Checkout.Details.DeliveryDetails.Title') }}
                    </h2>
                  </v-col>

                  <template v-if="userIsAuthenticated">
                    <v-col
                      v-if="userHasAddresses && addressModel.Lat && addressModel.Lng"
                      cols="12"
                    >
                      <v-card outlined>
                        <v-card-text v-if="!isCustomAddress(address) && userHasAddresses">
                          <v-map
                            :address-marker="{lat: parseFloat(address.Lat), lng: parseFloat(address.Lng)}"
                            :map-center="{lat: parseFloat(address.Lat), lng: parseFloat(address.Lng)}"
                            height="250"
                            map-static
                            show-address-marker
                            width="1200"
                          />
                        </v-card-text>

                        <v-card-text
                          class="pt-0 align-start"
                          style="min-height: 100px"
                        >
                          <v-container
                            fluid
                            pa-0
                          >
                            <v-row dense>
                              <v-col cols="12">
                                <v-chip
                                  v-if="address.Nickname"
                                  class="mr-1"
                                  color="pink"
                                  label
                                  small
                                  text-color="white"
                                >
                                  {{ address.Nickname }}
                                </v-chip>

                                <v-chip
                                  v-if="address.IsDefault"
                                  class="mr-1"
                                  color="success"
                                  label
                                  small
                                  text-color="white"
                                >
                                  {{ $t('Account.Addresses.Default') }}
                                </v-chip>
                              </v-col>

                              <v-col cols="12">
                                <h5 class="text-body-1 font-weight-bold">
                                  {{ getAddressFormatted(address) }}
                                </h5>
                              </v-col>

                              <v-col cols="12">
                                <div
                                  v-if="address.Floor"
                                  class="pr-2 d-inline-flex text-body-2"
                                  style="white-space: nowrap;"
                                >
                                  <h5 class="d-inline-flex text-body-2 font-weight-bold">
                                    {{ $t('Account.Addresses.Floor') }}:
                                  </h5>&nbsp;{{ address.Floor }}
                                </div>

                                <div
                                  v-if="address.DoorBell"
                                  class="d-inline-flex text-body-2"
                                  style="white-space: nowrap;"
                                >
                                  <h5 class="d-inline-flex text-body-2 font-weight-bold">
                                    {{ $t('Account.Addresses.DoorBell') }}:
                                  </h5>&nbsp;{{ address.DoorBell }}
                                </div>

                                <div
                                  v-if="address.Comment"
                                  class="d-flex text-body-2"
                                  style="white-space: nowrap;"
                                >
                                  <h5 class="d-inline-block text-body-2 font-weight-bold">
                                    {{ $t('Account.Addresses.Comment') }}:
                                  </h5>&nbsp;{{ address.Comment }}
                                </div>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>

                        <v-divider class="mx-4" />

                        <v-card-actions :class="{'d-block mx-2': $vuetify.breakpoint.smAndDown}">
                          <v-spacer />

                          <v-btn
                            :block="$vuetify.breakpoint.smAndDown"
                            class="my-1 pr-3 ml-0 mx-1"
                            color="brand-primary"
                            dark
                            depressed
                            large
                            @click="addAddress"
                          >
                            <v-icon left>
                              {{ $t('Account.Addresses.Button.Add.Icon') }}
                            </v-icon>
                            {{ $t('Account.Addresses.Button.Add.Title') }}
                          </v-btn>

                          <v-btn
                            v-if="userAddresses.length > 1"
                            :block="$vuetify.breakpoint.smAndDown"
                            class="my-1 pr-3 ml-0 mx-1"
                            color="brand-primary"
                            dark
                            depressed
                            large
                            @click="selectAddress"
                          >
                            <v-icon left>
                              {{ $t('Account.Addresses.Button.Change.Icon') }}
                            </v-icon>
                            {{ $t('Account.Addresses.Button.Change.Title') }}
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>

                    <template v-if="userIsAuthenticated && (!userHasAddresses || !addressModel.Lat)">
                      <v-col cols="12">
                        <div class="text-center">
                          <div class="mb-6">
                            <v-icon
                              color="brand-primary"
                              size="128"
                            >
                              mdi-map-marker-off
                            </v-icon>
                          </div>

                          <h2>{{ $t('Account.Addresses.NoAddresses.Title') }}</h2>

                          <h4 class="font-weight-regular mt-4">
                            {{ $t('Account.Addresses.NoAddresses.Subtitle') }}
                          </h4>

                          <v-btn
                            :large="$vuetify.breakpoint.xsOnly"
                            :x-large="$vuetify.breakpoint.smAndUp"
                            block
                            class="my-4"
                            color="brand-primary brand-green-dark--text"
                            dark
                            depressed
                            @click="addAddress"
                          >
                            <span class="brand-text-title">{{ $t('Account.Addresses.Button.Add.Title') }}</span>
                          </v-btn>
                        </div>
                      </v-col>
                    </template>
                  </template>

                  <template v-else>
                    <v-col
                      v-if="!isCustomAddress(address) && !addressModel.useCustomAddress"
                      cols="12"
                    >
                      <v-map-autocomplete-service
                        v-model="selectedLocation"
                        :address-marker="markerAddress"
                        :country="locationDeliveryCountriesStr"
                        :disabled="addressModel.useCustomAddress"
                        :geocode-access-token="geocodeProvider.Key"
                        :geocode-provider="geocodeProvider.Name"
                        :geolocate="true"
                        :hint="$t('Checkout.Details.DeliveryDetails.Field.Autocomplete.Hint')"
                        :map="!isCustomAddress(address) && !addressModel.useCustomAddress"
                        :map-center="mapCenter"
                        :maps-access-token="interactiveMapsProvider.Key"
                        :maps-provider="interactiveMapsProvider.Name"
                        :placeholder="$t('Checkout.Details.DeliveryDetails.Field.Autocomplete.Label')"
                        :rules="addressModelValidatorVuetifyFormFieldRules('AutoComplete')"
                        :types="['geocode']"
                        color="brand-primary"
                        display-field="Name"
                        map-width="100%"
                        @place-changed="(placeObj) => setAddressFromPlace(placeObj)"
                      />
                    </v-col>


                    <template v-if="addressModel.Formatted && !addressModel.useCustomAddress && !acceptLatLngAddresses">
                      <v-col cols="12">
                        <v-alert
                          border="left"
                          icon="mdi-alert-circle-outline"
                          text
                          type="warning"
                        >
                          {{ $t('Checkout.Details.DeliveryDetails.Notification.Confirm') }}
                        </v-alert>
                      </v-col>

                      <v-col cols="6">
                        <v-map-autocomplete-service
                          v-model="selectedCity"
                          :country="locationDeliveryCountriesStr"
                          :geocode-access-token="geocodeProvider.Key"
                          :geocode-provider="geocodeProvider.Name"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.City.Hint')"
                          :placeholder="$t('Checkout.Details.DeliveryDetails.Field.City.Label')"
                          :rules="addressModelValidatorVuetifyFormFieldRules('City')"
                          :types="['city']"
                          color="brand-primary"
                          display-field="Properties.City"
                          @place-changed="(placeObj) => setAddressFromPlace(placeObj)"
                        />
                      </v-col>

                      <v-col cols="6">
                        <v-map-autocomplete-service
                          v-model="selectedStreet"
                          :country="locationDeliveryCountriesStr"
                          :geocode-access-token="geocodeProvider.Key"
                          :geocode-provider="geocodeProvider.Name"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.Street.Hint')"
                          :placeholder="$t('Checkout.Details.DeliveryDetails.Field.Street.Label')"
                          :rules="addressModelValidatorVuetifyFormFieldRules('Street')"
                          :types="['address']"
                          color="brand-primary"
                          display-field="Properties.Street"
                          @place-changed="(placeObj) => setAddressFromPlace(placeObj)"
                        />
                      </v-col>

                      <v-col cols="6">
                        <v-text-field
                          v-model="addressModel.Postcode"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.Postcode.Hint')"
                          :label="$t('Checkout.Details.DeliveryDetails.Field.Postcode.Label')"
                          :rules="addressModelValidatorVuetifyFormFieldRules('Postcode')"
                          filled
                          prepend-inner-icon="mdi-map-marker"
                        />
                      </v-col>

                      <v-col cols="6">
                        <v-text-field
                          v-model="addressModel.Number"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.Number.Hint')"
                          :label="$t('Checkout.Details.DeliveryDetails.Field.Number.Label')"
                          :rules="addressModelValidatorVuetifyFormFieldRules('Number')"
                          filled
                          prepend-inner-icon="mdi-map-marker"
                        />
                      </v-col>
                    </template>

                    <template v-else-if="addressModel.useCustomAddress || acceptLatLngAddresses">
                      <v-col cols="12">
                        <v-alert
                          border="left"
                          icon="mdi-alert-circle-outline"
                          text
                          type="warning"
                        >
                          {{
                            acceptLatLngAddresses ?
                              $t('Checkout.Details.DeliveryDetails.Notification.ConfirmLatLngAddress') :
                              $t('Checkout.Details.DeliveryDetails.Notification.Confirm')
                          }}
                        </v-alert>
                      </v-col>

                      <v-col cols="6">
                        <v-text-field
                          ref="map_area"
                          v-model="addressModel.City"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.City.Hint')"
                          :label="$t('Checkout.Details.DeliveryDetails.Field.City.Label')"
                          :rules="acceptLatLngAddresses ? [] : addressModelValidatorVuetifyFormFieldRules('City')"
                          filled
                          prepend-inner-icon="mdi-map-marker"
                          @blur="confirmAddress"
                        />
                      </v-col>

                      <v-col cols="6">
                        <v-text-field
                          ref="map_street"
                          v-model="addressModel.Street"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.Street.Hint')"
                          :label="$t('Checkout.Details.DeliveryDetails.Field.Street.Label')"
                          :rules="acceptLatLngAddresses ? [] : addressModelValidatorVuetifyFormFieldRules('Street')"
                          filled
                          prepend-inner-icon="mdi-map-marker"
                          @blur="confirmAddress"
                        />
                      </v-col>

                      <v-col cols="6">
                        <v-text-field
                          v-model="addressModel.Postcode"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.Postcode.Hint')"
                          :label="$t('Checkout.Details.DeliveryDetails.Field.Postcode.Label')"
                          :rules="acceptLatLngAddresses ? [] : addressModelValidatorVuetifyFormFieldRules('Postcode')"
                          filled
                          prepend-inner-icon="mdi-map-marker"
                          @blur="confirmAddress"
                        />
                      </v-col>

                      <v-col cols="6">
                        <v-text-field
                          v-model="addressModel.Number"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.Number.Hint')"
                          :label="$t('Checkout.Details.DeliveryDetails.Field.Number.Label')"
                          :rules="acceptLatLngAddresses ? [] : addressModelValidatorVuetifyFormFieldRules('Number')"
                          filled
                          prepend-inner-icon="mdi-map-marker"
                          @blur="confirmAddress"
                        />
                      </v-col>
                    </template>

                    <template
                      v-if="(addressModel.Formatted && !addressModel.useCustomAddress && !acceptLatLngAddresses) || (addressModel.useCustomAddress || acceptLatLngAddresses)"
                    >
                      <v-col cols="6">
                        <v-text-field
                          v-model="addressModel.Floor"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.Floor.Hint')"
                          :label="$t('Checkout.Details.DeliveryDetails.Field.Floor.Label')"
                          filled
                          prepend-inner-icon="mdi-domain"
                          @blur="confirmAddress"
                        />
                      </v-col>

                      <v-col cols="6">
                        <v-text-field
                          v-model="addressModel.DoorBell"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.DoorBell.Hint')"
                          :label="$t('Checkout.Details.DeliveryDetails.Field.DoorBell.Label')"
                          filled
                          prepend-inner-icon="mdi-bell"
                          @blur="confirmAddress"
                        />
                      </v-col>

                      <v-col cols="12">
                        <v-textarea
                          v-model="addressModel.Comment"
                          :hint="$t('Checkout.Details.DeliveryDetails.Field.AddressComments.Hint')"
                          :label="$t('Checkout.Details.DeliveryDetails.Field.AddressComments.Label')"
                          auto-grow
                          filled
                          prepend-inner-icon="mdi-message-reply-text"
                          rows="3"
                          @blur="confirmAddress"
                        />
                      </v-col>
                    </template>
                  </template>
                </v-row>

                <!-- Order Type -->
                <v-row v-if="canShowOrderType">
                  <v-col cols="12">
                    <order-type-selector class="pa-0" />
                  </v-col>
                </v-row>
              </template>

              <v-row>
                <v-col cols="12">
                  <h2 class="d-inline-block text-h6 text-sm-h5 font-weight-bold">
                    {{ $t('Checkout.Details.OrderComments.Title') }}
                  </h2>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="model.Comment"
                    :hint="$t('Checkout.Details.OrderComments.Field.Comments.Hint')"
                    :label="$t('Checkout.Details.OrderComments.Field.Comments.Label')"
                    :placeholder="$t('Checkout.Details.OrderComments.Field.Comments.Placeholder')"
                    :rules="model.validator.vuetifyFormFieldRules('Comment')"
                    auto-grow
                    color="brand-primary"
                    filled
                    hide-details="auto"
                    rows="3"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-col>
      </v-row>

      <!-- Payment Methods -->
      <v-row>
        <v-col
          cols="12"
          md="8"
          offset-md="2"
          offset-sm="1"
          sm="10"
        >
          <select-payment-method
            v-model="selectedPaymentMethod"
            :title="$t('Checkout.Details.PaymentDetails.Title')"
          />
        </v-col>
      </v-row>

      <!-- Payment Gateways-->
      <v-row>
        <v-col
          cols="12"
          md="8"
          offset-md="2"
          offset-sm="1"
          sm="10"
        >
          <template v-if="isMinimumOrderTotalValid && cartProductsCount">
            <template v-if="AppType.isQRScanApp && !AppQrData && AppConfig.Marketplace.SelectQrDataRequired">
              <v-btn
                :large="$vuetify.breakpoint.xsOnly"
                :loading="loading"
                :x-large="$vuetify.breakpoint.smAndUp"
                block
                class="pl-2 pr-3"
                color="brand-primary"
                depressed
                @click="onSelectTable"
              >
                <span class="brand-text-title brand-green-dark--text">{{ $t('Common.SelectQrItemDialog.Subtitle') }}</span>
              </v-btn>
            </template>

            <template v-else>
              <generic-payment-gateway
                v-if="selectedPaymentMethod && selectedPaymentMethod.Code === PaymentTypeEnum.CASH"
                :model="customerModel"
              />

              <viva-smart-payment-gateway
                v-if="selectedPaymentMethod && selectedPaymentMethod.Code === PaymentTypeEnum.VIVA"
                :model="customerModel"
              />
            </template>
          </template>

          <payment-minimum-order-warning
            v-else
            :dense="$vuetify.breakpoint.xsOnly"
          />
        </v-col>
      </v-row>

      <template v-if="userIsAuthenticated && AppConfig.Marketplace.SupportsDelivery && AppCart.OrderType === OrderTypeEnum.DELIVERY">
        <account-select-address-dialog
          :visible.sync="selectAddressDialogVisible"
          @address:selected="onAddressSelected"
        />

        <account-add-edit-address-dialog
          :visible.sync="addEditAddressDialogVisible"
          @form:save:success="onAddressSaveSuccess"
        />
      </template>
    </template>
  </v-container>
</template>

<script>
import CheckoutDetailsMixin from '@/theme/default/views/checkout/CheckoutDetails.js'

export default {
  name  : 'CheckoutDetails',
  mixins: [CheckoutDetailsMixin]
}
</script>

<style scoped src="@/theme/default/views/checkout/CheckoutDetails.css"></style>
<style scoped>
</style>
